// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';

import { css, cx } from '@emotion/css';
import { Theme, useTheme } from '@mui/material';

// TYPES ---------------------------------------------------------------- //

interface FeatureProps {
  /**
   * How the routed surface uses the region's height. `page` (the default) is block flow that
   * scrolls in the layout's own scroll container. `none` is for the full-bleed surfaces (the
   * maps) that manage their own panes: the region becomes a flex column that clips, so a child
   * asking to fill — the product shell's `fill` mode — has a definite height to fill.
   *
   * The dealer says the same thing to `AppShellPage`; the embed has no app shell, so it lands
   * here. Both read one list (`isFullBleedPath`), so a route is full-bleed in both or neither.
   */
  scroll?: 'page' | 'none';
  children: React.ReactNode;
}

// COMPONENT ---------------------------------------------------------------- //

const Feature = ({ scroll = 'page', children }: FeatureProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);

  return <main className={cx(cls.feature, scroll === 'none' && cls.fullBleed)}>{children}</main>;
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme?: Theme) => {
  const styles = {
    feature: css`
      position: relative;
      width: 100%;
      height: 100%;
      background-color: ${theme?.palette?.background?.default};
    `,
    // `min-height: 0` lets the flex children shrink below their content height, which is what
    // stops a map's own scroll from pushing the region taller than the viewport.
    fullBleed: css`
      display: flex;
      flex-direction: column;
      min-height: 0;
      overflow: hidden;
    `,
  };

  return styles;
};

// EXPORT ---------------------------------------------------------------- //

export default Feature;
